// =========================================================================
// Turning Client rows into contact-picker options. Pure — this is where the
// picker decides what the user reads, so it is worth testing on its own.
// Split out of ClientSelectWithModal.tsx (module-size).
// =========================================================================

import type { Client } from "@/lib/types/database";

/** Sentinel option value for the "Add new contact named X" inline-create row. */
export const ADD_NEW_CONTACT_VALUE = "__add_new_contact__";

/** Minimum typed characters before the inline-create row is offered. */
export const MIN_INLINE_CREATE_LENGTH = 2;

export interface EnhancedSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  description?: string;
  searchableText?: string;
  email?: string;
  phone?: string;
  title?: string;
  status?: string;
  companyName?: string;
  renderDetails?: () => React.ReactNode;
}

/**
 * Build a clearly-placeholder, guaranteed-undeliverable email from a typed name
 * so a contact can be created inline from just a name. `.invalid` is a reserved
 * TLD (RFC 2606) — it never resolves — and "placeholder" makes the intent
 * obvious so the user updates it later.
 */
export function placeholderEmailFromName(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ".")
    .replace(/^\.+|\.+$/g, "");
  return `${slug || "contact"}@placeholder.invalid`;
}

/**
 * Map contacts to picker options.
 *
 * This is a CONTACT (person) picker — the PERSON's name is the primary label.
 * Company, title, email, phone render as secondary metadata via
 * ContactOptionPreview. Do NOT lead with company_name: that showed the org
 * twice + the email instead of the person's name.
 */
export function buildContactOptions(
  source: Client[],
  selectedId: string,
  fallbackItems: Client[] | undefined,
): EnhancedSelectOption[] {
  let list = source;

  // Ensure the pre-selected value (which may only exist in the `items` prop)
  // is present, so an edit form never renders a blank trigger.
  if (selectedId && fallbackItems?.length) {
    const valueId = String(selectedId);
    if (!list.some((c) => String(c.id) === valueId)) {
      const selectedFromItems = fallbackItems.find((c) => String(c.id) === valueId);
      if (selectedFromItems) list = [selectedFromItems, ...list];
    }
  }

  return list
    .filter((c) => c?.id)
    .map<EnhancedSelectOption>((c) => ({
      value: String(c.id),
      label: c.name || c.company_name || `Contact ${c.id}`,
      searchableText: [c.name, c.company_name, c.email, c.phone, c.title]
        .filter(Boolean)
        .join(" "),
      email: c.email,
      phone: c.phone ?? undefined,
      title: c.title ?? undefined,
      status: c.status ?? undefined,
      companyName: c.company_name ?? undefined,
    }));
}

/**
 * Whether to offer "Add new contact "X"". Only once enough has been typed, only
 * when nothing already carries that exact name, and never while a create is in
 * flight (which would let the user fire a duplicate).
 */
export function shouldOfferInlineCreate(
  trimmedSearch: string,
  options: EnhancedSelectOption[],
  createPending: boolean,
): boolean {
  if (trimmedSearch.length < MIN_INLINE_CREATE_LENGTH || createPending) return false;
  const lowered = trimmedSearch.toLowerCase();
  return !options.some((o) => o.label.trim().toLowerCase() === lowered);
}
